import React, { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { ValidationRule } from '../types';
import { dangerIconButton, helpClass, inputClass, labelClass, secondaryButton } from '../ui';
import { DraftEntry, DraftSequence, newUid, parseValues } from './draft';
import { ENTRY_KIND_LABELS } from './types';

const FIELD_TYPES = ['number', 'text', 'textarea', 'select', 'date', 'datetime', 'time', 'email', 'url', 'tel'];
const RULE_TYPES: ValidationRule['type'][] = ['required', 'min', 'max', 'min_length', 'max_length', 'regex', 'number', 'integer', 'one_of'];
const NUMERIC_RULES = new Set<ValidationRule['type']>(['min', 'max', 'min_length', 'max_length']);

function Row({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) {
  return (
    <label className="block">
      <span className={labelClass}>{label}</span>
      {children}
      {hint && <span className={helpClass}>{hint}</span>}
    </label>
  );
}

/** Validation rules of one entry or one point sequence. Rows are index-keyed, as in the form field editor. */
function RulesEditor({ rules, onChange }: { rules: ValidationRule[]; onChange: (rules: ValidationRule[]) => void }) {
  const setRule = (index: number, patch: Partial<ValidationRule>) => onChange(rules.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  return (
    <div className="space-y-2">
      {rules.length === 0 && <p className="text-[11px] text-slate-400">No validation rules.</p>}
      {rules.map((rule, i) => (
        <div className="grid grid-cols-[1fr_0.8fr_1.2fr_32px] gap-1.5" key={i}>
          <select className={inputClass()} value={rule.type} onChange={(e) => setRule(i, { type: e.target.value as ValidationRule['type'] })} aria-label="Rule type">
            {RULE_TYPES.map((t) => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
          </select>
          <input className={inputClass()} placeholder="Value" aria-label="Rule value" value={rule.value == null ? '' : String(rule.value)}
            onChange={(e) => {
              const raw = e.target.value;
              setRule(i, { value: NUMERIC_RULES.has(rule.type) && raw.trim() !== '' && Number.isFinite(Number(raw)) ? Number(raw) : raw });
            }} />
          <input className={inputClass()} placeholder="Custom message" aria-label="Rule message" value={rule.message || ''} onChange={(e) => setRule(i, { message: e.target.value })} />
          <button type="button" className={dangerIconButton} aria-label="Remove rule" onClick={() => onChange(rules.filter((_, j) => j !== i))}><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      ))}
      <button type="button" className={secondaryButton} onClick={() => onChange([...rules, { type: 'required', value: true }])}><Plus className="h-3.5 w-3.5" /> Add rule</button>
    </div>
  );
}

function SequenceRow({ sequence, onChange, onRemove }: { sequence: DraftSequence; onChange: (next: DraftSequence) => void; onRemove: () => void }) {
  // The text is kept while typing; numbers are only committed when every part parses.
  const [text, setText] = useState(sequence.values.join(', '));
  const parsed = parseValues(text);
  return (
    <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50/60 p-3">
      <div className="grid grid-cols-[1fr_2fr_32px] gap-2">
        <Row label="Sequence key" hint="Becomes the key suffix: increasing → inc, decreasing → dec, secondIncreasing → inc2.">
          <input className={inputClass()} value={sequence.key} onChange={(e) => onChange({ ...sequence, key: e.target.value })} />
        </Row>
        <Row label="Values" hint={parsed ? `${parsed.length} value${parsed.length === 1 ? '' : 's'}, separated by commas` : 'Enter numbers separated by commas'}>
          <input className={inputClass(!parsed)} value={text} aria-invalid={!parsed}
            onChange={(e) => {
              setText(e.target.value);
              const next = parseValues(e.target.value);
              if (next) onChange({ ...sequence, values: next });
            }} />
        </Row>
        <button type="button" className={`${dangerIconButton} mt-6`} aria-label="Remove sequence" onClick={onRemove}><Trash2 className="h-3.5 w-3.5" /></button>
      </div>
      <label className="flex items-center gap-2 text-[12px] text-slate-700">
        <input type="checkbox" checked={!!sequence.range_scaled} onChange={(e) => onChange({ ...sequence, range_scaled: e.target.checked })} />
        Scale values by the sensor range (value × range)
      </label>
      <div>
        <div className={labelClass}>Validation for every point of this sequence</div>
        <RulesEditor rules={sequence.validation || []} onChange={(validation) => onChange({ ...sequence, validation })} />
      </div>
    </div>
  );
}

export default function EntryEditor({ entry, problems, onChange }: { entry: DraftEntry; problems: string[]; onChange: (next: DraftEntry) => void }) {
  const patch = (p: Partial<DraftEntry>) => onChange({ ...entry, ...p });
  const sequences = entry.meta?.sequences || [];
  const setSequences = (next: DraftSequence[]) => patch({ meta: { ...entry.meta, sequences: next } });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600">{ENTRY_KIND_LABELS[entry.kind]}</span>
        <label className="ml-auto flex items-center gap-2 text-[12px] text-slate-700">
          <input type="checkbox" checked={entry.is_active} onChange={(e) => patch({ is_active: e.target.checked })} />
          Active <span className="text-slate-400">(inactive entries are not offered or served)</span>
        </label>
      </div>

      {problems.length > 0 && (
        <ul role="alert" className="space-y-0.5 rounded-lg border border-red-200 bg-red-50 p-2.5 text-[11px] text-red-700">
          {problems.map((p) => <li key={p}>• {p}</li>)}
        </ul>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <Row label="Key" hint={entry.kind === 'points' ? 'Forms select the whole group by this key.' : 'The value is submitted under this key. Changing it affects forms that already select it.'}>
          <input className={inputClass(problems.some((p) => p.startsWith('Key')))} value={entry.key} onChange={(e) => patch({ key: e.target.value })} />
        </Row>
        <Row label="Label">
          <input className={inputClass(problems.some((p) => p.startsWith('Label')))} value={entry.label} onChange={(e) => patch({ label: e.target.value })} />
        </Row>
        <Row label={entry.kind === 'points' ? 'Input type' : 'Type'}>
          <select className={inputClass()} value={entry.field_type} onChange={(e) => patch({ field_type: e.target.value })}>
            {(FIELD_TYPES.includes(entry.field_type) ? FIELD_TYPES : [entry.field_type, ...FIELD_TYPES]).map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </Row>
        <Row label="Placeholder">
          <input className={inputClass()} value={entry.placeholder} onChange={(e) => patch({ placeholder: e.target.value })} />
        </Row>
      </div>

      {entry.kind === 'points' ? (
        <section>
          <div className="mb-2 flex items-center justify-between">
            <div>
              <h3 className="text-[13px] font-semibold text-slate-800">Point sequences</h3>
              <p className={helpClass}>Expanded when the form runs, from the sensor range chosen on the form. Field keys follow the name the calibration endpoint expects, for example point_40_0_inc.</p>
            </div>
            <button type="button" className={secondaryButton} onClick={() => setSequences([...sequences, { _uid: newUid(), key: '', values: [], range_scaled: true }])}><Plus className="h-3.5 w-3.5" /> Add sequence</button>
          </div>
          <div className="space-y-2">
            {sequences.map((s, i) => (
              <SequenceRow key={s._uid} sequence={s} onChange={(next) => setSequences(sequences.map((x, j) => (j === i ? next : x)))} onRemove={() => setSequences(sequences.filter((_, j) => j !== i))} />
            ))}
          </div>
        </section>
      ) : (
        <section>
          <h3 className="mb-2 text-[13px] font-semibold text-slate-800">Validation rules</h3>
          <RulesEditor rules={entry.validation} onChange={(validation) => patch({ validation })} />
        </section>
      )}
    </div>
  );
}
